import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { StickyNote } from '../../types';
import './CopyMergeModal.css';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';

interface CopyMergeModalProps {
  sourceNote: StickyNote;
  messageIndex: number;
  messageText: string;
  isOpen: boolean;
  onClose: () => void;
  onCopyMerge: (targetNoteId: string, editedMessage: string) => void;
}

const CopyMergeModal: React.FC<CopyMergeModalProps> = ({
  sourceNote,
  messageIndex,
  messageText,
  isOpen,
  onClose,
  onCopyMerge,
}) => {
  const [editedMessage, setEditedMessage] = useState(messageText);
  const [targetNoteId, setTargetNoteId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allNotes = useWhiteboardStore((state) => state.state.notes);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEditedMessage(messageText);
      setTargetNoteId('');
      setError(null);
    }
  }, [isOpen, messageText]);

  const handleCopyMerge = async () => {
    if (!targetNoteId) {
      setError('Please select a target note');
      return;
    }

    if (!editedMessage.trim()) {
      setError('Message cannot be empty');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onCopyMerge(targetNoteId, editedMessage.trim());
      onClose();
    } catch (err) {
      console.error('Error during copy merge:', err);
      setError(err instanceof Error ? err.message : 'Failed to copy message');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const availableNotes = allNotes.filter(note => note.id !== sourceNote.id);

  const modalContent = (
    <div className="copy-merge-modal-overlay" onClick={onClose}>
      <div className="copy-merge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="copy-merge-modal-header">
          <h2>Copy Message</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="copy-merge-modal-content">
          <div className="note-info">
            <div className="note-details">
              <h3>From: {sourceNote.topic || 'Untitled'}</h3>
            </div>
            <div className="note-details">
              <h3>To:</h3>
              <select
                className="target-note-dropdown"
                value={targetNoteId}
                onChange={e => setTargetNoteId(e.target.value)}
              >
                <option value="">Select target note...</option>
                {availableNotes.map(note => (
                  <option key={note.id} value={note.id}>
                    {note.topic || 'Untitled'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="message-section">
            <h3>Message to Copy</h3>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="message-textarea-container">
              <div className="message-edit-section">
                <h4>Edit Message (Optional):</h4>
                <textarea
                  className="message-textarea"
                  value={editedMessage}
                  onChange={(e) => setEditedMessage(e.target.value)}
                  placeholder="Edit the message if needed..."
                  rows={6}
                />
              </div>
              
              <div className="message-preview">
                <h4>Preview:</h4>
                <div className="preview-content">
                  {editedMessage ? (
                    <ReactMarkdown>{editedMessage}</ReactMarkdown>
                  ) : (
                    <div className="no-message">No message to preview</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="copy-merge-modal-actions">
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="copy-merge-modal-button"
              onClick={handleCopyMerge}
              disabled={isLoading || !targetNoteId || !editedMessage.trim()}
            >
              {isLoading ? 'Copying...' : 'Copy Message'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CopyMergeModal;
